import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { PublicSettings } from '@projektarbeit/shared';
import { apiFetch } from '@/lib/api-client';
import {
  bulkPutCacheEntries,
  countScanQueue,
  enqueueScan,
  getCacheEntry,
  getMeta,
  type CacheEntry,
} from '@/lib/idb';
import { processScanQueue } from '@/lib/scan-queue';

export const KIOSK_TOKEN_KEY = 'kiosk-token';
export const SCAN_TIMEOUT_MS = 10_000;

export function getKioskToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(KIOSK_TOKEN_KEY);
}

export function setKioskToken(token: string): void {
  window.localStorage.setItem(KIOSK_TOKEN_KEY, token);
}

export function clearKioskToken(): void {
  window.localStorage.removeItem(KIOSK_TOKEN_KEY);
}

export interface KioskSelf {
  roomId: number;
  roomName: string;
  kioskName: string;
  /**
   * Active project day window (Europe/Berlin wall-clock) — null when no PD
   * is running right now. Drives the kiosk-header PA countdown.
   */
  currentProjectDay: {
    date: string;
    startTime: string;
    endTime: string;
  } | null;
}

export function useKioskMe(token: string | null) {
  return useQuery<KioskSelf, Error>({
    queryKey: ['kiosk-me', token],
    enabled: Boolean(token),
    queryFn: () =>
      apiFetch<KioskSelf>('/kiosk/me', {
        bearerToken: token ?? undefined,
      }),
    staleTime: 5 * 60_000,
  });
}

// PublicSettings now lives in @projektarbeit/shared (ROADMAP #32) — re-exported
// here so existing `@/hooks/useKiosk` import sites keep working.
export type { PublicSettings };

export function usePublicSettings() {
  return useQuery<PublicSettings, Error>({
    queryKey: ['public-settings'],
    queryFn: () => apiFetch<PublicSettings>('/public/settings'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Subset of AppSettings exposed via /public/settings — extended in later
 * prompts. For now, kiosk only needs dawdle thresholds. The kiosk endpoint
 * intentionally does NOT pull settings from `/public/settings` (those are
 * for the login screen); instead we read them from the scan-response or
 * fall back to the canonical defaults. We stash the defaults here so the
 * dawdle classifier always has something to work with.
 */
export interface KioskDawdleSettings {
  dawdleWarnMinutes: number;
  dawdleAlertMinutes: number;
}

export const DEFAULT_DAWDLE_SETTINGS: KioskDawdleSettings = {
  dawdleWarnMinutes: 5,
  dawdleAlertMinutes: 10,
};

export interface ScanRequestBody {
  barcode: string;
}

export interface ScanResponse {
  action: 'check-in' | 'check-out' | 'room-switch' | 'already-checked-in' | 'still-checked-out';
  student: {
    id: number;
    firstName: string;
    lastName: string;
    className: string;
  };
  room: { id: number; name: string };
  previousRoom: { id: number; name: string } | null;
  status: string;
  dawdleAtCheckin: number | null;
  timestamp: string;
}

/**
 * Kiosk scan mutation. Sends the kiosk Bearer token from localStorage and
 * gives up after SCAN_TIMEOUT_MS — the kiosk overlay will queue the scan and
 * surface "Scan erfasst — wird gleich übertragen" (5xx) or the network-branch
 * copy in KioskPage.tsx.
 */
export function useScanMutation() {
  return useMutation<ScanResponse, Error, ScanRequestBody>({
    mutationFn: async (body) => {
      const token = getKioskToken();
      if (!token) {
        throw new Error('Kein Kiosk-Token im localStorage.');
      }
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), SCAN_TIMEOUT_MS);
      try {
        return await apiFetch<ScanResponse>('/scan', {
          method: 'POST',
          body,
          bearerToken: token,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
    },
    retry: false,
  });
}

/** True iff `navigator.onLine === true` (treats unknown as online). */
export function isOnlineNow(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

/**
 * Re-emits `online` / `offline` events as React state. Defaults to `true`
 * during SSR / when navigator is missing.
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => isOnlineNow());
  useEffect(() => {
    const onOnline = (): void => setOnline(true);
    const onOffline = (): void => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);
  return online;
}

/** Look up a cached fullName for a barcode. Returns null on cache miss. */
export async function lookupBarcodeCache(barcode: string): Promise<CacheEntry | null> {
  try {
    return await getCacheEntry(barcode);
  } catch {
    return null;
  }
}

/** Persist a scan in the offline queue. Resolves with the queued row id. */
export async function enqueueOfflineScan(barcode: string): Promise<number> {
  const now = Date.now();
  // Fresh enqueues are eligible immediately — `nextAttemptAt = now`. The
  // backoff schedule only kicks in after the first failure.
  return enqueueScan({ barcode, timestamp: now, retryCount: 0, nextAttemptAt: now });
}

interface KioskStudentCacheResponse {
  entries: CacheEntry[];
}

/**
 * One-shot fetch of the active-student cache. Persisted into the IndexedDB
 * `cache` store so optimistic overlays survive offline reloads.
 */
export async function refreshStudentCache(token: string): Promise<number> {
  const data = await apiFetch<KioskStudentCacheResponse>('/kiosk/student-cache', {
    bearerToken: token,
  });
  await bulkPutCacheEntries(data.entries);
  return data.entries.length;
}

export const STUDENT_CACHE_REFRESH_MS = 6 * 60 * 60 * 1000;

export function useStudentCachePrefill(token: string | null): void {
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = async (): Promise<void> => {
      try {
        await refreshStudentCache(token);
      } catch {
        // Silent: a 401/network error here is not user-visible. Cache is
        // best-effort; the next tick will retry.
      }
    };

    void tick();
    timer = setInterval(() => {
      if (cancelled) return;
      void tick();
    }, STUDENT_CACHE_REFRESH_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [token]);
}

export interface QueueStatus {
  pending: number;
  lastSyncAt: number | null;
}

/**
 * Reactive view of the queue status. Polls every 2s when the queue is
 * non-empty and on `online`/`offline` events.
 */
export function useQueueStatus(): QueueStatus {
  const [status, setStatus] = useState<QueueStatus>({
    pending: 0,
    lastSyncAt: null,
  });

  useEffect(() => {
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      try {
        const [pending, lastSyncAt] = await Promise.all([countScanQueue(), getMeta('lastSyncAt')]);
        if (cancelled) return;
        setStatus({ pending, lastSyncAt: lastSyncAt ?? null });
      } catch {
        if (cancelled) return;
        setStatus({ pending: 0, lastSyncAt: null });
      }
    };

    void refresh();

    const handleStatus = (): void => {
      void refresh();
    };
    window.addEventListener('pa-queue-changed', handleStatus);
    window.addEventListener('online', handleStatus);
    window.addEventListener('offline', handleStatus);
    const interval = setInterval(handleStatus, 2_000);

    return () => {
      cancelled = true;
      window.removeEventListener('pa-queue-changed', handleStatus);
      window.removeEventListener('online', handleStatus);
      window.removeEventListener('offline', handleStatus);
      clearInterval(interval);
    };
  }, []);

  return status;
}

/** Notify queue-status listeners that the queue may have changed. */
export function notifyQueueChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('pa-queue-changed'));
}

/**
 * Background-sync loop. Drains the queue whenever we come online. Used as
 * the Safari fallback for the (unsupported) Service-Worker Background Sync
 * API.
 *
 * Polls every 10 s. The interval was 30 s before per-item exponential
 * backoff existed — back then a short interval meant retry-hammering the
 * backend. Now the per-item `nextAttemptAt` gate (scan-queue.ts) decides
 * when each item is eligible, so the interval only needs to be small
 * enough to pick items up promptly as their backoff windows expire. Cheap
 * when the queue is empty: a single `listScanQueue()` IDB read.
 */
export const SYNC_INTERVAL_MS = 10_000;

export function useScanQueueSync(): void {
  useEffect(() => {
    let cancelled = false;

    const drain = async (): Promise<void> => {
      if (!isOnlineNow()) return;
      try {
        const result = await processScanQueue();
        if (cancelled) return;
        if (result.succeeded + result.removedClient + result.dropped > 0) {
          notifyQueueChanged();
        }
      } catch {
        // Best-effort; the next tick retries.
      }
    };

    void drain();
    const interval = setInterval(drain, SYNC_INTERVAL_MS);
    const onOnline = (): void => {
      void drain();
    };
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
    };
  }, []);
}
